// src/hooks/useAuthInit.ts
// Runs ONCE on app mount.
// Uses the httpOnly refresh cookie to silently restore session.
// If cookie is valid and not expired → user stays logged in without re-entering credentials.
// If cookie missing or expired (7 days passed) → redirect to login.

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { authService } from 'services/authService';
import { setCredentials, setToken, setInitialized } from 'store/slices/authSlice';

export function useAuthInit(): void {
  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        // 1. Hit /auth/refresh — browser automatically sends the httpOnly cookie.
        //    If the refresh token is valid and not expired (within 7 days), we get
        //    a new access token back. If expired → 401 → catch block → setInitialized.
        const { accessToken } = await authService.refresh();
        if (cancelled) return;

        // 2. Use the fresh access token to fetch user profile. `api`'s request
        //    interceptor reads the token from Redux (not from the global
        //    `axios` singleton's defaults — `api` is a separate instance made
        //    via axios.create(), so setting axios.defaults here never reached
        //    it), so the token must land in the store first or this call goes
        //    out with no Authorization header and 401s.
        dispatch(setToken(accessToken));
        const user = await authService.me();
        if (cancelled) return;

        // 3. Hydrate the Redux store — app is now authenticated.
        dispatch(setCredentials({ user, accessToken }));

      } catch {
        // Refresh token missing, expired (> 7 days), or revoked → user must login.
        if (!cancelled) dispatch(setInitialized());
      }
    }

    init();

    return () => { cancelled = true; };
  }, [dispatch]);
}
